import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { CssBaseline } from "@mui/material";
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from "react-router-dom";
import { lazy, Suspense } from "react";
import { Login } from "src/features/app-shell/pages/Login.tsx";
import Root from "src/features/app-shell/pages/Root.tsx";
import { PageLoadingIndicator } from "src/shared/common/PageLoadingIndicator";
import { ProtectedRoute } from "@Auth/ProtectedRoute.tsx";
import { ThemeProviderWrapper } from "@styles/ThemeProviderWrapper.tsx";
import { AuthProvider } from "@Auth/AuthProvider.tsx";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "src/lib/queryClient";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { Dashboard } from "src/features/dashboard/pages/Dashboard.tsx";
import { Stages } from "src/features/stocks/pages/Stages/Stages.tsx";
import * as Sentry from "@sentry/react";
import AllOrders from "src/features/orders/pages/AllOrders.tsx";
import { registerSW } from "virtual:pwa-register";

Sentry.init({
  dsn: "https://45be92108625ab52496ba6c5a1b570e6@o4509045494448128.ingest.us.sentry.io/4509045495693312",
});

registerSW({ immediate: true });

ModuleRegistry.registerModules([AllCommunityModule]);

// Lazy load components for better performance
const SignUp = lazy(() => import("src/features/app-shell/pages/SignUp.tsx"));
const Home = lazy(() => import("src/features/products/pages/Index.tsx"));
const AllProducts = lazy(
  () => import("src/features/products/pages/AllProducts.tsx")
);
const AllTags = lazy(() => import("src/features/tags/pages/AllTags.tsx"));
const ProductDetails = lazy(
  () =>
    import("src/features/products/pages/ProductDetails/ProductDetails.tsx")
);
const Scanner = lazy(() => import("src/features/scanner/pages/Scanner.tsx"));
const Locations = lazy(() => import("src/features/locations/pages/Index.tsx"));
const AllLocations = lazy(
  () => import("src/features/locations/pages/AllLocations.tsx")
);
const LocationDetails = lazy(
  () => import("src/features/locations/pages/LocationDetails.tsx")
);
const InventoryHub = lazy(
  () => import("src/features/inventory/pages/InventoryHub.tsx")
);
const Stocks = lazy(() => import("src/features/stocks/pages/Index.tsx"));
const AllStocks = lazy(() => import("src/features/stocks/pages/AllStocks.tsx"));
const UserSettingsPage = lazy(
  () => import("src/features/user-settings/pages/UserSettings.tsx")
);
const UserPreferences = lazy(
  () => import("src/features/user-settings/pages/UserPreferences.tsx")
);
const OrganizationPreferences = lazy(
  () => import("src/features/user-settings/pages/OrganizationPreferences.tsx")
);
const Billing = lazy(() => import("src/features/billing/pages/Billing.tsx"));
const Orders = lazy(() => import("src/features/orders/pages/Orders.tsx"));
const OrderDetail = lazy(
  () => import("src/features/orders/pages/OrderDetail.tsx")
);
const BackorderDashboard = lazy(
  () => import("src/features/orders/pages/BackorderDashboard.tsx")
);
const FulfillmentDashboard = lazy(
  () => import("src/features/orders/pages/FulfillmentDashboard.tsx")
);
const Customers = lazy(() => import("src/features/customers/pages/Index.tsx"));
const AllCustomers = lazy(
  () => import("src/features/customers/pages/AllCustomers.tsx")
);
const CustomerDetail = lazy(
  () => import("src/features/customers/pages/CustomerDetail.tsx")
);
const Suppliers = lazy(() => import("src/features/suppliers/pages/Index.tsx"));
const AllSuppliers = lazy(
  () => import("src/features/suppliers/pages/AllSuppliers.tsx")
);
const SupplierDetail = lazy(
  () => import("src/features/suppliers/pages/SupplierDetail.tsx")
);
const PurchaseOrders = lazy(
  () => import("src/features/purchase-orders/pages/Index.tsx")
);
const AllPurchaseOrders = lazy(
  () => import("src/features/purchase-orders/pages/AllPurchaseOrders.tsx")
);
const PurchaseOrderDetail = lazy(
  () => import("src/features/purchase-orders/pages/PurchaseOrderDetail.tsx")
);

const PageNotFound = lazy(
  () => import("src/features/app-shell/pages/PageNotFound.tsx")
);

const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    children: [
      // Public routes
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "sign-up",
        element: (
          <Suspense fallback={<PageLoadingIndicator />}>
            <SignUp />
          </Suspense>
        ),
      },
      // Protected routes
      {
        path: "/",
        element: (
          <ProtectedRoute>
            <App />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/products/all-products" replace />,
          },

          {
            path: "scanner",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Scanner />
              </Suspense>
            ),
          },
          //Dashboard section
          {
            path: "dashboard",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Dashboard />
              </Suspense>
            ),
          },
          // Products section
          {
            path: "products",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Home />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="all-products" replace />,
              },
              {
                path: "all-products",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllProducts />
                  </Suspense>
                ),
              },
              {
                path: "product/:id",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <ProductDetails />
                  </Suspense>
                ),
              },
              {
                path: "all-tags",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllTags />
                  </Suspense>
                ),
              },
            ],
          },
          // Inventory hub (phase 1: tree + placeholder; runs alongside /locations)
          {
            path: "inventory",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <InventoryHub />
              </Suspense>
            ),
          },
          {
            path: "inventory/folder/:locationId",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <InventoryHub />
              </Suspense>
            ),
          },
          {
            // Item detail opened from a folder (keeps the arrival location).
            path: "inventory/folder/:locationId/item/:productId",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <InventoryHub />
              </Suspense>
            ),
          },
          {
            // Item detail without folder context (e.g. from global search).
            path: "inventory/item/:productId",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <InventoryHub />
              </Suspense>
            ),
          },
          // Locations section
          {
            path: "locations",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Locations />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="all-locations" replace />,
              },
              {
                path: "location/:id",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <LocationDetails />
                  </Suspense>
                ),
              },
              {
                path: "all-locations",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllLocations />
                  </Suspense>
                ),
              },
            ],
          },
          // Stocks section
          {
            path: "stocks",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Stocks />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="all-stocks" replace />,
              },
              {
                path: "stages",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <Stages />
                  </Suspense>
                ),
              },
              {
                path: "all-stocks",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllStocks />
                  </Suspense>
                ),
              },
            ],
          },
          // Settings section
          {
            path: "settings",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <UserSettingsPage />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="user-preferences" replace />,
              },
              {
                path: "user-preferences",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <UserPreferences />
                  </Suspense>
                ),
              },
              {
                path: "organization-preferences",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <OrganizationPreferences />
                  </Suspense>
                ),
              },
              {
                path: "billing",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <Billing />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "orders",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Orders />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="all-orders" replace />,
              },

              {
                path: "all-orders",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllOrders />
                  </Suspense>
                ),
              },
              {
                path: "order/:id",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <OrderDetail />
                  </Suspense>
                ),
              },
              {
                path: "all-backorders",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <BackorderDashboard />
                  </Suspense>
                ),
              },
              {
                path: "fulfillment",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <FulfillmentDashboard />
                  </Suspense>
                ),
              },
            ],
          },
          // Customers section
          {
            path: "customers",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Customers />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="all-customers" replace />,
              },
              {
                path: "all-customers",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllCustomers />
                  </Suspense>
                ),
              },
              {
                path: "customer/:id",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <CustomerDetail />
                  </Suspense>
                ),
              },
            ],
          },
          // Suppliers section
          {
            path: "suppliers",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Suppliers />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="all-suppliers" replace />,
              },
              {
                path: "all-suppliers",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllSuppliers />
                  </Suspense>
                ),
              },
              {
                path: "supplier/:id",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <SupplierDetail />
                  </Suspense>
                ),
              },
            ],
          },
          // Purchase Orders section
          {
            path: "purchase-orders",
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <PurchaseOrders />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="all-purchase-orders" replace />,
              },
              {
                path: "all-purchase-orders",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllPurchaseOrders />
                  </Suspense>
                ),
              },
              {
                path: "po/:id",
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <PurchaseOrderDetail />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: (
      <Suspense fallback={<PageLoadingIndicator />}>
        <PageNotFound />
      </Suspense>
    ),
  },
]);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ThemeProviderWrapper>
        <CssBaseline />
        <RouterProvider router={router} />
      </ThemeProviderWrapper>
    </AuthProvider>
    {import.meta.env.DEV && import.meta.env.VITE_REACT_QUERY_DEVTOOLS === "true" && (
      <ReactQueryDevtools initialIsOpen={false} />
    )}
  </QueryClientProvider>
);
